import type { Form, Transition, WorkflowDetail, WorkflowDraft } from '@servicetim/shared';
import {
  useProjectRoles,
  usePublishWorkflow,
  useSaveWorkflowDraft,
  useWorkflowVersions,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  Input,
  LoadingState,
  ProblemBanner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@servicetim/ui/components';
import { GitBranch, History, Save, Upload } from 'lucide-react';
import { lazy, Suspense, useEffect, useId, useMemo, useState } from 'react';
import { useFieldCatalog } from '../../projects/catalog/field-catalog';
import { fieldErrorsOf, messageAt, remainingErrors } from '../../projects/lib/field-errors';
import { FormRow } from '../../projects/ui/form-row';
import { Section } from '../../projects/ui/section';
import { draftEquals, statusNames, updateTransition, validateDraft } from './draft-utils';
import { RuleDrawer } from './rule-drawer';
import { parseStatusMappingError, StatusMappingDialog } from './status-mapping-dialog';
import { StatusesTable } from './statuses-table';
import { TransitionsTable } from './transitions-table';
import { VersionsPanel } from './versions-panel';

const WorkflowGraph = lazy(() => import('./workflow-graph'));

export interface WorkflowEditorProps {
  projectId: string;
  workflow: WorkflowDetail;
  transitionForms: readonly Form[];
}

/**
 * Table-driven workflow editor (ADR-0016 §1): local draft with save (`PUT draft`, errors by
 * path), publish (409 `workflow.status_mapping_required` opens the mapping dialog), versions
 * and a lazy read-only graph.
 */
export function WorkflowEditor({ projectId, workflow, transitionForms }: WorkflowEditorProps) {
  const id = useId();
  const catalog = useFieldCatalog(projectId);
  const roles = useProjectRoles(projectId);
  const versions = useWorkflowVersions(projectId, workflow.id);
  const save = useSaveWorkflowDraft(projectId);
  const publish = usePublishWorkflow(projectId);

  const [draft, setDraft] = useState<WorkflowDraft>(workflow.draft);
  const [clientErrors, setClientErrors] = useState<ReturnType<typeof validateDraft>>([]);
  const [rulesFor, setRulesFor] = useState<number | null>(null);
  const [mappingError, setMappingError] = useState<unknown>(null);
  const dirty = !draftEquals(draft, workflow.draft);

  // Adopt the server draft after saves / reverts / external changes when nothing is dirty here.
  // biome-ignore lint/correctness/useExhaustiveDependencies: resync from the saved draft only
  useEffect(() => {
    setDraft(workflow.draft);
    setClientErrors([]);
  }, [workflow.updatedAt]);

  const serverErrors = fieldErrorsOf(save.error);
  const errors = useMemo(() => [...clientErrors, ...serverErrors], [clientErrors, serverErrors]);
  const bannerErrors = remainingErrors(serverErrors, [
    'statuses',
    'transitions',
    'initialStatusId',
    'name',
  ]);

  const onSave = async () => {
    const issues = validateDraft(draft);
    setClientErrors(issues);
    if (issues.length > 0) {
      toast.error('The draft has structural problems', { description: issues[0]?.message });
      return;
    }
    try {
      await save.mutateAsync({ id: workflow.id, draft });
      toast.success('Draft saved');
    } catch {
      // errors rendered by path
    }
  };

  const onPublish = async (statusMapping?: Record<string, string>) => {
    try {
      await publish.mutateAsync({ id: workflow.id, statusMapping });
      setMappingError(null);
      toast.success(`Workflow "${workflow.name}" published`, {
        description: statusMapping
          ? 'A background job moves tickets out of the removed statuses.'
          : undefined,
      });
    } catch (error) {
      if (parseStatusMappingError(error)) {
        setMappingError(error);
      } else {
        setMappingError(null);
      }
    }
  };

  const removedStatusNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const v of versions.data ?? [])
      for (const s of v.snapshot.statuses) names[s.id] ??= s.name;
    return { ...names, ...statusNames(workflow.draft) };
  }, [versions.data, workflow.draft]);

  const missing = parseStatusMappingError(mappingError);
  const editingTransition: Transition | null =
    rulesFor !== null ? (draft.transitions[rulesFor] ?? null) : null;

  return (
    <div className="space-y-6">
      {save.error && bannerErrors.length > 0 ? (
        <ProblemBanner
          error={save.error}
          omitPaths={serverErrors.filter((e) => !bannerErrors.includes(e)).map((e) => e.path)}
          size="sm"
        />
      ) : null}
      {publish.error && !missing ? (
        <ProblemBanner error={publish.error} size="sm" title="Publish failed" />
      ) : null}
      <Section
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            <GitBranch className="size-4 text-brand-600" aria-hidden="true" />
            {workflow.name}
            {workflow.publishedVersion === null ? (
              <Badge variant="warning">Never published</Badge>
            ) : (
              <Badge variant="success">v{workflow.publishedVersion} live</Badge>
            )}
            {dirty ? (
              <Badge variant="info">Unsaved changes</Badge>
            ) : workflow.draftDirty ? (
              <Badge variant="secondary">
                Draft differs from v{workflow.publishedVersion ?? '–'}
              </Badge>
            ) : null}
          </span>
        }
        description="Edit statuses and transitions below, save the draft, then publish a new version. Tickets keep running on the published version until then."
        actions={
          <>
            <Button
              variant="outline"
              disabled={!dirty || save.isPending}
              onClick={() => setDraft(workflow.draft)}
            >
              Discard
            </Button>
            <Button
              variant="outline"
              disabled={!dirty || save.isPending}
              onClick={() => void onSave()}
            >
              <Save />
              Save draft
            </Button>
            <Button
              disabled={
                dirty ||
                publish.isPending ||
                (!workflow.draftDirty && workflow.publishedVersion !== null)
              }
              title={
                dirty
                  ? 'Save the draft first'
                  : !workflow.draftDirty && workflow.publishedVersion !== null
                    ? 'The draft is already published'
                    : undefined
              }
              onClick={() => void onPublish()}
            >
              <Upload />
              Publish
            </Button>
          </>
        }
      >
        <FormRow
          label="Name"
          htmlFor={`${id}-name`}
          required
          error={messageAt(serverErrors, 'name')}
          className="max-w-md"
        >
          <Input
            id={`${id}-name`}
            value={draft.name}
            maxLength={80}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </FormRow>
      </Section>

      <Tabs defaultValue="statuses">
        <TabsList>
          <TabsTrigger value="statuses">
            Statuses <Badge variant="secondary">{draft.statuses.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="transitions">
            Transitions <Badge variant="secondary">{draft.transitions.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="graph">Graph</TabsTrigger>
          <TabsTrigger value="versions">
            <History />
            Versions
          </TabsTrigger>
        </TabsList>
        <TabsContent value="statuses">
          <StatusesTable draft={draft} onChange={setDraft} errors={errors} />
        </TabsContent>
        <TabsContent value="transitions">
          <TransitionsTable
            draft={draft}
            onChange={setDraft}
            forms={transitionForms}
            errors={errors}
            onEditRules={setRulesFor}
          />
        </TabsContent>
        <TabsContent value="graph">
          <Suspense fallback={<LoadingState rows={4} label="Loading graph…" />}>
            <WorkflowGraph draft={draft} />
          </Suspense>
          <p className="mt-2 text-fg-muted text-xs">
            Read-only preview of the draft: columns are the status categories, the bold node is the
            initial status.
          </p>
        </TabsContent>
        <TabsContent value="versions">
          <VersionsPanel
            projectId={projectId}
            workflowId={workflow.id}
            publishedVersion={workflow.publishedVersion}
          />
        </TabsContent>
      </Tabs>

      <RuleDrawer
        transition={editingTransition}
        catalog={catalog}
        roles={roles.data ?? []}
        projectId={projectId}
        onApply={(next) => {
          if (rulesFor !== null) setDraft(updateTransition(draft, rulesFor, next));
          setRulesFor(null);
        }}
        onOpenChange={(open) => {
          if (!open) setRulesFor(null);
        }}
      />
      <StatusMappingDialog
        missing={missing}
        statusNames={removedStatusNames}
        targets={workflow.draft.statuses}
        onSubmit={(mapping) => onPublish(mapping)}
        onCancel={() => setMappingError(null)}
        busy={publish.isPending}
        error={publish.error}
      />
    </div>
  );
}
